import JSZip from 'jszip'
import STRINGS from 'utils/strings'
import { addUniqToArr } from 'utils/misc'
import { addMediaToFileMap, deleteMediaFromInstrumentJson } from 'utils/instrument'
import {
  QUESTION_TYPE_MAP,
  TIMED_MATCHING_GRID_DEFAULTS,
  TIMED_MATCHING_GRID_KEY_SLOT_COUNT,
  TIMED_MATCHING_GRID_MIN_IMAGE_SIZE,
  TIMED_MATCHING_GRID_MAX_IMAGE_SIZE,
  TIMED_MATCHING_GRID_MAX_IMAGE_BYTES,
  TIMED_MATCHING_GRID_MAX_SVG_BYTES,
  TIMED_MATCHING_GRID_INPUT_TYPE,
  TIMED_MATCHING_GRID_TIMER_DISPLAY,
  TIMED_MATCHING_GRID_KEY_MAPPING,
} from 'utils/constants'

export const getTimedMatchingGridDefaults = () => ({
  grid_rows: TIMED_MATCHING_GRID_DEFAULTS.gridRows,
  grid_columns: TIMED_MATCHING_GRID_DEFAULTS.gridColumns,
  time_limit_seconds: TIMED_MATCHING_GRID_DEFAULTS.timeLimitSeconds,
  timer_display: TIMED_MATCHING_GRID_DEFAULTS.timerDisplay,
  randomize_items: TIMED_MATCHING_GRID_DEFAULTS.randomizeItems,
  in_review: false,
  key_mapping: TIMED_MATCHING_GRID_DEFAULTS.keyMapping,
  input_type: TIMED_MATCHING_GRID_DEFAULTS.inputType,
  symbol_key: [],
  symbol_key_version: 0,
})

export const getTimedMatchingGridDetails = question => {
  const details = question?.attributes?.timed_matching_grid_details
  if (!details || typeof details !== 'object') {
    return getTimedMatchingGridDefaults()
  }
  return {
    ...getTimedMatchingGridDefaults(),
    ...details,
    in_review: false,
    key_mapping: TIMED_MATCHING_GRID_KEY_MAPPING.fixed19,
    symbol_key: Array.isArray(details.symbol_key) ? details.symbol_key : [],
  }
}

export const getGridCellCount = (rows, columns) => {
  const r = Number(rows) || 0
  const c = Number(columns) || 0
  return Math.max(0, r) * Math.max(0, c)
}

export const getSizingConstraintNote = (rows, columns) => {
  const total = getGridCellCount(rows, columns)
  return `${total} items total — requires 1200 × 900 or larger; smaller screens block launch other than scroll`
}

export const clampGridDimension = value => {
  const num = parseInt(value, 10)
  if (Number.isNaN(num)) return TIMED_MATCHING_GRID_DEFAULTS.gridRows
  return Math.min(
    TIMED_MATCHING_GRID_DEFAULTS.gridMax,
    Math.max(TIMED_MATCHING_GRID_DEFAULTS.gridMin, num),
  )
}

/** Merge a patch into timed_matching_grid_details, keeping item_sequence in sync. */
export const applyTimedMatchingGridDetailsPatch = (
  details,
  patch,
  { clearSequenceOnRandomize = false } = {},
) => {
  const nextDetails = {
    ...details,
    ...patch,
    in_review: false,
  }

  if (
    Object.prototype.hasOwnProperty.call(patch, 'grid_rows') ||
    Object.prototype.hasOwnProperty.call(patch, 'grid_columns')
  ) {
    if (nextDetails.randomize_items === false) {
      nextDetails.item_sequence = resizeItemSequence(
        nextDetails.item_sequence,
        nextDetails.grid_rows,
        nextDetails.grid_columns,
      )
    }
  }

  if (clearSequenceOnRandomize && nextDetails.randomize_items) {
    delete nextDetails.item_sequence
  } else if (
    Object.prototype.hasOwnProperty.call(patch, 'randomize_items') &&
    nextDetails.randomize_items === false &&
    !Array.isArray(nextDetails.item_sequence)
  ) {
    nextDetails.item_sequence = getEmptyItemSequence(nextDetails.grid_rows, nextDetails.grid_columns)
  }

  if (Object.prototype.hasOwnProperty.call(patch, 'symbol_key')) {
    return withClearedItemSequenceForNewKey(nextDetails)
  }

  return nextDetails
}

/** New ZIP / symbol key invalidates authored placements. */
export const withClearedItemSequenceForNewKey = details => {
  const next = { ...details }
  if (next.randomize_items === false) {
    next.item_sequence = getEmptyItemSequence(next.grid_rows, next.grid_columns)
  } else {
    delete next.item_sequence
  }
  return next
}

/**
 * An interrupted first upload has no key to keep, so Next stays disabled.
 * An interrupted replace keeps the previous complete key, so Next stays enabled.
 */
export const interruptedSymbolUploadBlocksNext = symbolKey => !hasCompleteSymbolKey(symbolKey)

export const hasCompleteSymbolKey = (symbolKey, slotCount = TIMED_MATCHING_GRID_KEY_SLOT_COUNT) => {
  if (!Array.isArray(symbolKey) || symbolKey.length !== slotCount) return false
  return symbolKey.every(
    entry =>
      entry &&
      Number(entry.digit) >= 1 &&
      Number(entry.digit) <= slotCount &&
      !!(entry.image || entry.imageSrc || (typeof entry.character === 'string' && entry.character.trim())),
  )
}

const NON_BLOCKING_TMG_SAVE_ERRORS = ['tmgItemSequenceErr']

/** Incomplete manual sequences warn in the builder but must not block Save/Next. */
export const omitNonBlockingTmgSaveErrors = questionErrors => {
  if (!questionErrors || typeof questionErrors !== 'object') return questionErrors
  const next = { ...questionErrors }
  NON_BLOCKING_TMG_SAVE_ERRORS.forEach(key => {
    delete next[key]
  })
  return next
}

export const omitNonBlockingTmgSurveyErrors = errors => {
  if (!errors?.questions) return errors
  const questions = {}
  Object.entries(errors.questions).forEach(([id, qErrors]) => {
    const next = omitNonBlockingTmgSaveErrors(qErrors)
    if (next && Object.keys(next).length > 0) questions[id] = next
  })
  return { ...errors, questions }
}

export const getSymbolKeyEntry = (symbolKey, digit) =>
  (symbolKey || []).find(entry => Number(entry.digit) === Number(digit)) || null

/** Prefer a local blob preview, then the persisted survey image id. */
export const getSymbolKeyDisplaySrc = entry => {
  if (!entry) return null
  const src = entry.imageSrc
  if (src && String(src).startsWith('blob:')) return src
  if (entry.image) return entry.image
  if (src && !String(src).startsWith('blob:')) return src
  return null
}

export const getEmptyItemSequence = (rows, columns) => {
  const total = getGridCellCount(rows, columns)
  return Array(total).fill(null)
}

const symbolEntryHasImage = entry => !!(entry?.image || entry?.imageSrc)

/** Blob previews with no server image cannot be shown once the upload dialog closes. */
export const symbolEntryHasUnusableBlob = entry =>
  !!(
    entry?.imageSrc &&
    String(entry.imageSrc).startsWith('blob:') &&
    !entry.image &&
    !(typeof entry.character === 'string' && entry.character.trim())
  )

/**
 * Blocking symbol-key problem for one timed matching grid.
 * A partial or blob-only image set is treated as corrupted so the author must re-upload.
 */
export const getTimedMatchingGridSymbolKeyError = details => {
  if (!details) return STRINGS.tmgSymbolKeyRequiredErr
  const symbolKey = details.symbol_key
  const hasUnusableBlob =
    Array.isArray(symbolKey) && symbolKey.some(entry => symbolEntryHasUnusableBlob(entry))
  if (details.symbol_key_corrupted || hasUnusableBlob) return STRINGS.tmgSymbolKeyCorruptedErr
  if (hasCompleteSymbolKey(symbolKey)) return null
  const hasAnyImage = Array.isArray(symbolKey) && symbolKey.some(symbolEntryHasImage)
  if (hasAnyImage) return STRINGS.tmgSymbolKeyCorruptedErr
  return STRINGS.tmgSymbolKeyRequiredErr
}

export const getSymbolKeyIdentity = symbolKey =>
  (Array.isArray(symbolKey) ? symbolKey : [])
    .map(
      entry =>
        `${Number(entry?.digit)}:${entry?.image || ''}:${
          typeof entry?.character === 'string' ? entry.character : ''
        }`,
    )
    .join('|')

/**
 * Plan putting the pre-replace symbol key back after an interrupted upload.
 * Returns null when the current key is already that snapshot.
 */
export const planTimedMatchingGridSymbolKeyRestore = (currentDetails, snapshot) => {
  const currentKey = Array.isArray(currentDetails?.symbol_key) ? currentDetails.symbol_key : []
  const previousKey = Array.isArray(snapshot?.symbol_key) ? snapshot.symbol_key : []
  if (getSymbolKeyIdentity(currentKey) === getSymbolKeyIdentity(previousKey)) return null

  const previousIds = new Set(previousKey.map(entry => entry?.image).filter(Boolean))
  const removeImageIds = []
  currentKey.forEach(entry => {
    if (entry?.image && !previousIds.has(entry.image) && !removeImageIds.includes(entry.image)) {
      removeImageIds.push(entry.image)
    }
  })

  const nextDetails = {
    ...(currentDetails || {}),
    symbol_key: previousKey.map(entry => ({ ...entry })),
    symbol_key_version: snapshot?.symbol_key_version ?? currentDetails?.symbol_key_version ?? 0,
    in_review: false,
  }
  delete nextDetails.symbol_key_corrupted
  if (snapshot?.hasItemSequence) {
    nextDetails.item_sequence = Array.isArray(snapshot.item_sequence)
      ? [...snapshot.item_sequence]
      : snapshot.item_sequence
  }

  return {
    nextDetails,
    removeImageIds,
    addImageIds: [...previousIds],
  }
}

/** Apply a restore plan onto an instrument question. Returns false when nothing changed. */
export const applyTimedMatchingGridSymbolKeyRestore = (instrument, itemId, snapshot) => {
  const question = instrument?.questions?.[itemId]
  if (!question) return false
  const plan = planTimedMatchingGridSymbolKeyRestore(
    question.attributes?.timed_matching_grid_details,
    snapshot,
  )
  if (!plan) return false

  plan.removeImageIds.forEach(fileId => {
    deleteMediaFromInstrumentJson({ _instrument: instrument, fileId, itemId })
  })

  const attributes = { ...(question.attributes || {}) }
  attributes.timed_matching_grid_details = plan.nextDetails
  attributes.in_review = false
  question.attributes = attributes

  plan.addImageIds.forEach(fileId => {
    instrument.image_list = instrument.image_list ? addUniqToArr(instrument.image_list, fileId) : [fileId]
    addMediaToFileMap({ _instrument: instrument, fileId, itemId })
  })
  return true
}

/**
 * True when any timed matching grid question is missing its symbol key or has a corrupted one,
 * so navigation can be routed through validation even when nothing else changed.
 */
export const hasIncompleteTimedMatchingGridKey = questions => {
  if (!questions) return false
  return Object.values(questions).some(question => {
    if (question?.type !== QUESTION_TYPE_MAP.timedMatchingGrid) return false
    return !!getTimedMatchingGridSymbolKeyError(getTimedMatchingGridDetails(question))
  })
}

const hasBlockingErrorsBesidesMissingSymbolKey = errors => {
  if (!errors || typeof errors !== 'object') return false
  if (errors.scores && Object.keys(errors.scores).length > 0) return true
  const otherSurveyKeys = Object.keys(errors).filter(key => key !== 'questions' && key !== 'scores')
  if (otherSurveyKeys.length > 0) return true
  return Object.values(errors.questions || {}).some(qErrors => {
    if (!qErrors || typeof qErrors !== 'object') return false
    return Object.keys(qErrors).some(key => key !== 'tmgSymbolKeyErr')
  })
}

/**
 * Next/save toast for a missing TMG ZIP. Other instrument errors take priority;
 * this copy is shown only after those are cleared.
 */
export const getTmgMissingSymbolKeyToast = (questions, errors) => {
  if (!hasIncompleteTimedMatchingGridKey(questions)) return null
  if (hasBlockingErrorsBesidesMissingSymbolKey(errors)) return null
  const match = Object.values(questions || {}).find(question => {
    if (question?.type !== QUESTION_TYPE_MAP.timedMatchingGrid) return false
    return !!getTimedMatchingGridSymbolKeyError(getTimedMatchingGridDetails(question))
  })
  return getTimedMatchingGridSymbolKeyError(getTimedMatchingGridDetails(match)) || STRINGS.tmgSymbolKeyRequiredErr
}

export const getTimedMatchingGridItemSequenceWarning = details => {
  if (!details || details.randomize_items !== false) return ''
  const { grid_rows: rows, grid_columns: columns, item_sequence: itemSequence } = details
  if (isItemSequenceComplete(itemSequence, rows, columns)) return ''
  const total = getGridCellCount(rows, columns)
  const placed = countPlacedItems(itemSequence)
  return (
    `Item sequence incomplete (${placed} of ${total} placed). ` +
    `The form can't be published until all ${total} are placed.`
  )
}

export const getFirstIncompleteTimedMatchingGridSequenceWarning = questions => {
  if (!questions) return ''
  const match = Object.values(questions).find(question => {
    if (question?.type !== QUESTION_TYPE_MAP.timedMatchingGrid) return false
    return !!getTimedMatchingGridItemSequenceWarning(getTimedMatchingGridDetails(question))
  })
  return match ? getTimedMatchingGridItemSequenceWarning(getTimedMatchingGridDetails(match)) : ''
}

export const hasIncompleteTimedMatchingGridSequence = questions =>
  !!getFirstIncompleteTimedMatchingGridSequenceWarning(questions)

export const countPlacedItems = sequence => {
  if (!Array.isArray(sequence)) return 0
  return sequence.filter(v => v !== null && v !== undefined && v !== '').length
}

export const isItemSequenceComplete = (sequence, rows, columns) => {
  const total = getGridCellCount(rows, columns)
  if (!Array.isArray(sequence) || sequence.length !== total) return false
  return sequence.every(v => {
    const n = Number(v)
    return Number.isInteger(n) && n >= 1 && n <= TIMED_MATCHING_GRID_KEY_SLOT_COUNT
  })
}

export const getSymbolBalanceTargets = (totalCells, slotCount = TIMED_MATCHING_GRID_KEY_SLOT_COUNT) => {
  const base = Math.floor(totalCells / slotCount)
  const remainder = totalCells % slotCount
  const targets = {}
  for (let digit = 1; digit <= slotCount; digit += 1) {
    targets[digit] = base + (digit <= remainder ? 1 : 0)
  }
  return targets
}

export const getSymbolBalanceCounts = (sequence, slotCount = TIMED_MATCHING_GRID_KEY_SLOT_COUNT) => {
  const counts = {}
  for (let digit = 1; digit <= slotCount; digit += 1) counts[digit] = 0
  if (!Array.isArray(sequence)) return counts
  sequence.forEach(value => {
    const n = Number(value)
    if (Number.isInteger(n) && n >= 1 && n <= slotCount) counts[n] += 1
  })
  return counts
}

/** Digits whose placed count exceeds the balanced target. Advisory only — not a save blocker. */
export const getOverusedBalanceDigits = (sequence, totalCells, slotCount = TIMED_MATCHING_GRID_KEY_SLOT_COUNT) => {
  const counts = getSymbolBalanceCounts(sequence, slotCount)
  const targets = getSymbolBalanceTargets(totalCells, slotCount)
  const overused = []
  for (let digit = 1; digit <= slotCount; digit += 1) {
    if (counts[digit] > targets[digit]) overused.push(digit)
  }
  return overused
}

/**
 * Generates one fixed, roughly balanced sequence (not per-administration shuffle).
 */
export const generateBalancedItemSequence = (rows, columns, slotCount = TIMED_MATCHING_GRID_KEY_SLOT_COUNT) => {
  const total = getGridCellCount(rows, columns)
  const targets = getSymbolBalanceTargets(total, slotCount)
  const pool = []
  for (let digit = 1; digit <= slotCount; digit += 1) {
    for (let i = 0; i < targets[digit]; i += 1) pool.push(digit)
  }
  // Fisher–Yates shuffle for a single fixed authored sequence
  for (let i = pool.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1))
    ;[pool[i], pool[j]] = [pool[j], pool[i]]
  }
  return pool
}

/** Sequence used to populate a read-only participant-style preview. */
export const getTimedMatchingGridPreviewSequence = details => {
  const rows = Number(details?.grid_rows) || 0
  const columns = Number(details?.grid_columns) || 0
  const total = getGridCellCount(rows, columns)
  if (details?.randomize_items) {
    return generateBalancedItemSequence(rows, columns)
  }
  const authored = Array.isArray(details?.item_sequence) ? details.item_sequence : []
  return Array.from({ length: total }, (_, i) => {
    const n = Number(authored[i])
    return Number.isInteger(n) && n >= 1 && n <= TIMED_MATCHING_GRID_KEY_SLOT_COUNT ? n : null
  })
}

export const parseItemSequenceCsv = (csvText, rows, columns) => {
  const total = getGridCellCount(rows, columns)
  if (!csvText || typeof csvText !== 'string') {
    return { ok: false, error: 'CSV file is empty or unreadable.' }
  }

  const tokens = csvText
    .trim()
    .split(/[\s,;]+/)
    .map(t => t.trim())
    .filter(Boolean)

  if (tokens.length !== total) {
    return {
      ok: false,
      error:
        `CSV must contain exactly ${total} values (got ${tokens.length}). Import rejected.`,
    }
  }

  const sequence = []
  for (let i = 0; i < tokens.length; i += 1) {
    const n = Number(tokens[i])
    if (!Number.isInteger(n) || n < 1 || n > TIMED_MATCHING_GRID_KEY_SLOT_COUNT) {
      return {
        ok: false,
        error:
          `Invalid value "${tokens[i]}" at position ${i + 1}. ` +
          `Values must be digits 1–${TIMED_MATCHING_GRID_KEY_SLOT_COUNT}. Import rejected.`,
      }
    }
    sequence.push(n)
  }

  return { ok: true, sequence }
}

const SYMBOL_FILE_NAME_RE = /^(\d+)\.(svg|png)$/i

export const getExpectedSymbolFilenames = (slotCount = TIMED_MATCHING_GRID_KEY_SLOT_COUNT) => {
  const names = []
  for (let i = 1; i <= slotCount; i += 1) {
    names.push(`${i}.svg`, `${i}.png`)
  }
  return names
}

const inspectSymbolImage = (fileBlob, { checkTransparency = false } = {}) =>
  new Promise((resolve, reject) => {
    const url = URL.createObjectURL(fileBlob)
    const img = new Image()
    img.onload = () => {
      const { naturalWidth: width, naturalHeight: height } = img
      let hasTransparentPixels = null
      if (checkTransparency) {
        hasTransparentPixels = false
        try {
          if (width > 0 && height > 0 && typeof document !== 'undefined') {
            const canvas = document.createElement('canvas')
            canvas.width = width
            canvas.height = height
            const ctx = canvas.getContext('2d', { willReadFrequently: true })
            if (ctx) {
              ctx.drawImage(img, 0, 0)
              const { data } = ctx.getImageData(0, 0, width, height)
              hasTransparentPixels = pngPixelDataHasTransparency(data)
            }
          }
        } catch (e) {
          hasTransparentPixels = false
        }
      }
      URL.revokeObjectURL(url)
      resolve({ width, height, hasTransparentPixels })
    }
    img.onerror = () => {
      URL.revokeObjectURL(url)
      reject(new Error('Unable to read image dimensions.'))
    }
    img.src = url
  })

/** True if any pixel is not fully opaque (alpha < 255). */
export const pngPixelDataHasTransparency = data => {
  if (!data || data.length < 4) return false
  for (let i = 3; i < data.length; i += 4) {
    if (data[i] < 255) return true
  }
  return false
}

export const getSymbolPngTransparencyError = ({ hasTransparentPixels, fileName } = {}) => {
  if (hasTransparentPixels) return null
  const label = fileName || 'image'
  return `${label}: PNG must have a transparent background. Import rejected.`
}

const FORBIDDEN_SVG_TAGS = new Set([
  'script',
  'iframe',
  'foreignobject',
  'object',
  'embed',
  'link',
  'meta',
  'base',
  'video',
  'audio',
  'canvas',
])

const SVG_MAX_ELEMENT_DEPTH = 100
const SVG_SIZE_EPSILON = 0.01

const localName = node => String(node.localName || node.nodeName || '').toLowerCase()

const parseSvgLength = value => {
  if (value == null || String(value).trim() === '') return null
  const raw = String(value).trim()
  if (/%$/.test(raw)) return { kind: 'percent', value: parseFloat(raw) }
  const n = parseFloat(raw)
  if (!Number.isFinite(n) || n <= 0) return null
  return { kind: 'px', value: n }
}

const parseViewBox = value => {
  if (!value) return null
  const parts = String(value)
    .trim()
    .split(/[\s,]+/)
    .map(Number)
  if (parts.length !== 4 || parts.some(n => !Number.isFinite(n))) return null
  if (parts[2] <= 0 || parts[3] <= 0) return null
  return { minX: parts[0], minY: parts[1], width: parts[2], height: parts[3] }
}

const isSquareMeasure = (a, b) => Math.abs(Number(a) - Number(b)) <= SVG_SIZE_EPSILON

const isDisallowedSvgUrl = value => {
  if (!value) return false
  const raw = String(value).trim()
  if (!raw || raw.startsWith('#')) return false
  return /^(?:javascript|vbscript|data|file|https?):/i.test(raw) || /(?:^|[\s('"])(?:javascript|vbscript|data|file):/i.test(raw)
}

const isOpaqueFill = fill => {
  if (fill == null) return false
  const value = String(fill).trim().toLowerCase()
  if (!value || value === 'none' || value === 'transparent' || value === 'currentcolor') return false
  return true
}

const isFullCanvasBackgroundRect = (el, root) => {
  if (localName(el) !== 'rect') return false
  if (!isOpaqueFill(el.getAttribute('fill'))) return false
  const fillOpacity = parseFloat(el.getAttribute('fill-opacity') || el.getAttribute('opacity') || '1')
  if (Number.isFinite(fillOpacity) && fillOpacity <= 0) return false

  const x = parseFloat(el.getAttribute('x') || '0')
  const y = parseFloat(el.getAttribute('y') || '0')
  if ((Number.isFinite(x) && x !== 0) || (Number.isFinite(y) && y !== 0)) return false

  const widthAttr = el.getAttribute('width')
  const heightAttr = el.getAttribute('height')
  if (widthAttr === '100%' && heightAttr === '100%') return true

  const viewBox = parseViewBox(root.getAttribute('viewBox') || root.getAttribute('viewbox'))
  const w = parseFloat(widthAttr)
  const h = parseFloat(heightAttr)
  if (viewBox && w >= viewBox.width && h >= viewBox.height) return true

  const rootW = parseSvgLength(root.getAttribute('width'))
  const rootH = parseSvgLength(root.getAttribute('height'))
  if (rootW?.kind === 'px' && rootH?.kind === 'px' && w >= rootW.value && h >= rootH.value) {
    return true
  }
  return false
}

const walkSvgElements = (root, visit) => {
  const stack = [{ node: root, depth: 1 }]
  while (stack.length) {
    const { node, depth } = stack.pop()
    if (node.nodeType !== 1) continue
    if (depth > SVG_MAX_ELEMENT_DEPTH) {
      return 'SVG nests elements too deeply. Import rejected.'
    }
    const err = visit(node, depth)
    if (err) return err
    const children = node.children ? Array.from(node.children) : []
    for (let i = children.length - 1; i >= 0; i -= 1) {
      stack.push({ node: children[i], depth: depth + 1 })
    }
  }
  return null
}

/**
 * Practical SVG checks for symbol-key ZIPs: well-formed XML, square 1:1, optional 64–256px
 * width/height, no active content, no full-canvas opaque background.
 */
export const validateSymbolSvgMarkup = (svgText, fileName = 'image') => {
  const label = fileName || 'image'
  if (typeof svgText !== 'string' || !svgText.trim()) {
    return `${label}: SVG is empty or unreadable. Import rejected.`
  }
  if (/<!ENTITY/i.test(svgText)) {
    return `${label}: SVG must not contain entity declarations. Import rejected.`
  }

  const forbiddenTagMatch = svgText.match(
    /<\s*(script|iframe|foreignObject|object|embed|link|meta|base|video|audio|canvas)\b/i,
  )
  if (forbiddenTagMatch) {
    return `${label}: SVG contains forbidden element <${forbiddenTagMatch[1].toLowerCase()}>. Import rejected.`
  }

  if (typeof DOMParser === 'undefined') {
    return `${label}: unable to parse SVG. Import rejected.`
  }

  const doc = new DOMParser().parseFromString(svgText, 'image/svg+xml')
  const parseError = doc.querySelector('parsererror')
  if (parseError) {
    return `${label}: SVG is not valid XML. Import rejected.`
  }

  const root = doc.documentElement
  if (!root || localName(root) !== 'svg') {
    return `${label}: file is not a valid SVG. Import rejected.`
  }

  const viewBox = parseViewBox(root.getAttribute('viewBox') || root.getAttribute('viewbox'))
  const width = parseSvgLength(root.getAttribute('width'))
  const height = parseSvgLength(root.getAttribute('height'))

  if (!viewBox && !(width && height)) {
    return `${label}: SVG must declare a viewBox or an explicit width and height. Import rejected.`
  }

  if (viewBox && !isSquareMeasure(viewBox.width, viewBox.height)) {
    return `${label}: SVG must be square (1:1 viewBox). Import rejected.`
  }
  if (width?.kind === 'px' && height?.kind === 'px' && !isSquareMeasure(width.value, height.value)) {
    return `${label}: SVG must be square (width equals height). Import rejected.`
  }

  if (width?.kind === 'px' && height?.kind === 'px') {
    if (
      width.value < TIMED_MATCHING_GRID_MIN_IMAGE_SIZE ||
      height.value < TIMED_MATCHING_GRID_MIN_IMAGE_SIZE
    ) {
      return (
        `${label}: SVG width/height must be at least ` +
        `${TIMED_MATCHING_GRID_MIN_IMAGE_SIZE}×${TIMED_MATCHING_GRID_MIN_IMAGE_SIZE}px. Import rejected.`
      )
    }
    if (
      width.value > TIMED_MATCHING_GRID_MAX_IMAGE_SIZE ||
      height.value > TIMED_MATCHING_GRID_MAX_IMAGE_SIZE
    ) {
      return (
        `${label}: SVG width/height must be at most ` +
        `${TIMED_MATCHING_GRID_MAX_IMAGE_SIZE}×${TIMED_MATCHING_GRID_MAX_IMAGE_SIZE}px. Import rejected.`
      )
    }
  }

  const markupError = walkSvgElements(root, el => {
    const name = localName(el)
    if (FORBIDDEN_SVG_TAGS.has(name)) {
      return `${label}: SVG contains forbidden element <${name}>. Import rejected.`
    }
    if (el.attributes) {
      for (let i = 0; i < el.attributes.length; i += 1) {
        const attr = el.attributes[i]
        const attrName = String(attr.name || '').toLowerCase()
        const attrValue = attr.value
        if (attrName.startsWith('on')) {
          return `${label}: SVG must not contain event handlers (${attr.name}). Import rejected.`
        }
        if (
          ['href', 'xlink:href', 'src'].includes(attrName) &&
          isDisallowedSvgUrl(attrValue)
        ) {
          return `${label}: SVG must not reference external or scripted URLs. Import rejected.`
        }
      }
    }
    if (isFullCanvasBackgroundRect(el, root)) {
      return `${label}: SVG must have a transparent background (no full-canvas fill). Import rejected.`
    }
    return null
  })
  if (markupError) return markupError

  return null
}

/** PNG must be square and 64–256px. SVG has no pixel-size limit. */
export const getSymbolImageDimensionError = ({
  width,
  height,
  ext,
  fileName,
} = {}) => {
  const isPng = String(ext || '').toLowerCase() === 'png'
  if (!isPng) return null

  const w = Number(width)
  const h = Number(height)
  const label = fileName || 'image'

  if (!Number.isFinite(w) || !Number.isFinite(h) || w <= 0 || h <= 0) {
    return `${label}: unable to read image dimensions. Import rejected.`
  }

  if (isPng && w !== h) {
    return `${label}: PNG must be square. Import rejected.`
  }

  if (
    w < TIMED_MATCHING_GRID_MIN_IMAGE_SIZE ||
    h < TIMED_MATCHING_GRID_MIN_IMAGE_SIZE
  ) {
    return (
      `${label}: image must be at least ` +
      `${TIMED_MATCHING_GRID_MIN_IMAGE_SIZE}×${TIMED_MATCHING_GRID_MIN_IMAGE_SIZE}px. Import rejected.`
    )
  }

  if (
    w > TIMED_MATCHING_GRID_MAX_IMAGE_SIZE ||
    h > TIMED_MATCHING_GRID_MAX_IMAGE_SIZE
  ) {
    return (
      `${label}: image must be at most ` +
      `${TIMED_MATCHING_GRID_MAX_IMAGE_SIZE}×${TIMED_MATCHING_GRID_MAX_IMAGE_SIZE}px. Import rejected.`
    )
  }

  return null
}

/**
 * Validate a ZIP of symbol key images. All-or-nothing: any failure rejects the whole archive.
 * Expects exactly one file per digit 1..N named `{digit}.svg` or `{digit}.png`.
 */
export const validateSymbolKeyZip = async (zipFile, slotCount = TIMED_MATCHING_GRID_KEY_SLOT_COUNT) => {
  if (!zipFile || !zipFile.name) {
    return { ok: false, error: 'Please select a ZIP file.' }
  }

  const extension = `.${zipFile.name
    .split('.')
    .pop()
    .toLowerCase()}`
  if (extension !== '.zip') {
    return { ok: false, error: 'Please upload a .zip archive of symbol images.' }
  }

  let zip
  try {
    zip = await JSZip.loadAsync(zipFile)
  } catch (e) {
    return { ok: false, error: 'Unable to read ZIP file. The archive may be corrupt.' }
  }

  const entries = Object.keys(zip.files)
    .map(path => {
      const file = zip.files[path]
      if (file.dir) return null
      const baseName = path.split('/').pop()
      if (!baseName || baseName.startsWith('.')) return null
      return { path, baseName, file }
    })
    .filter(Boolean)

  if (entries.length === 0) {
    return { ok: false, error: 'ZIP is empty. Import rejected.' }
  }

  const byDigit = {}
  const unexpected = []

  entries.forEach(({ path, baseName, file }) => {
    const match = baseName.match(SYMBOL_FILE_NAME_RE)
    if (!match) {
      unexpected.push(baseName || path)
      return
    }
    const digit = Number(match[1])
    if (digit < 1 || digit > slotCount) {
      unexpected.push(baseName)
      return
    }
    if (byDigit[digit]) {
      unexpected.push(`${baseName} (duplicate for ${digit})`)
      return
    }
    byDigit[digit] = { digit, baseName, file, ext: match[2].toLowerCase() }
  })

  const missing = []
  for (let d = 1; d <= slotCount; d += 1) {
    if (!byDigit[d]) missing.push(d)
  }

  if (missing.length || unexpected.length || Object.keys(byDigit).length !== slotCount) {
    const parts = []
    if (missing.length) parts.push(`missing ${missing.join(', ')}`)
    if (unexpected.length) parts.push(`unexpected/extra: ${unexpected.join(', ')}`)
    return {
      ok: false,
      error:
        `ZIP must contain exactly files named 1.svg–${slotCount}.svg (or .png), ` +
        `one per digit (${parts.join('; ')}). Import rejected.`,
    }
  }

  const symbols = []
  /* Sequential validation is required for all-or-nothing ZIP rejection. */
  /* eslint-disable no-await-in-loop */
  for (let d = 1; d <= slotCount; d += 1) {
    const entry = byDigit[d]
    const blob = await entry.file.async('blob')
    const mime = entry.ext === 'svg' ? 'image/svg+xml' : 'image/png'
    const fileForUpload = new File([blob], entry.baseName, { type: mime })
    // JSZip blobs often lack a MIME type; SVGs need image/svg+xml to render in <img>
    const typedBlob = blob.type === mime ? blob : new Blob([blob], { type: mime })

    if (entry.ext === 'svg') {
      if (typedBlob.size > TIMED_MATCHING_GRID_MAX_SVG_BYTES) {
        return {
          ok: false,
          error: `${entry.baseName}: SVG must be 256 KB or smaller. Import rejected.`,
        }
      }
      let svgText
      try {
        svgText = await typedBlob.text()
      } catch (e) {
        return { ok: false, error: `${entry.baseName}: unable to read SVG. Import rejected.` }
      }
      const svgError = validateSymbolSvgMarkup(svgText, entry.baseName)
      if (svgError) {
        return { ok: false, error: svgError }
      }
    } else if (typedBlob.size > TIMED_MATCHING_GRID_MAX_IMAGE_BYTES) {
      return {
        ok: false,
        error: `${entry.baseName}: image must be 2.5 MB or smaller. Import rejected.`,
      }
    }

    let inspection
    try {
      inspection = await inspectSymbolImage(typedBlob, {
        checkTransparency: entry.ext === 'png',
      })
    } catch (e) {
      return { ok: false, error: `${entry.baseName}: unable to read image. Import rejected.` }
    }
    const dimensionError = getSymbolImageDimensionError({
      width: inspection.width,
      height: inspection.height,
      ext: entry.ext,
      fileName: entry.baseName,
    })
    if (dimensionError) {
      return { ok: false, error: dimensionError }
    }
    if (entry.ext === 'png') {
      const transparencyError = getSymbolPngTransparencyError({
        hasTransparentPixels: inspection.hasTransparentPixels,
        fileName: entry.baseName,
      })
      if (transparencyError) {
        return { ok: false, error: transparencyError }
      }
    }
    const previewUrl = URL.createObjectURL(typedBlob)
    symbols.push({
      digit: d,
      fileName: entry.baseName,
      file: fileForUpload,
      previewUrl,
      ext: entry.ext,
    })
  }
  /* eslint-enable no-await-in-loop */

  return {
    ok: true,
    symbols,
    statusText: `${slotCount} of ${slotCount} symbols found`,
  }
}

export const revokeSymbolPreviewUrls = symbols => {
  if (!Array.isArray(symbols)) return
  symbols.forEach(s => {
    if (s?.previewUrl) URL.revokeObjectURL(s.previewUrl)
  })
}

export const isValidTimerDisplay = value =>
  [
    TIMED_MATCHING_GRID_TIMER_DISPLAY.countdown,
    TIMED_MATCHING_GRID_TIMER_DISPLAY.countup,
  ].includes(value)

export const isValidInputType = value =>
  [
    TIMED_MATCHING_GRID_INPUT_TYPE.digit,
    TIMED_MATCHING_GRID_INPUT_TYPE.checkmark,
  ].includes(value)

export const validateTimedMatchingGridQuestion = question => {
  const errors = {}
  const details = getTimedMatchingGridDetails(question)
  const {
    grid_rows: rows,
    grid_columns: columns,
    time_limit_seconds: timeLimit,
    timer_display: timerDisplay,
    input_type: inputType,
  } = details

  if (
    !Number.isInteger(Number(rows)) ||
    Number(rows) < TIMED_MATCHING_GRID_DEFAULTS.gridMin ||
    Number(rows) > TIMED_MATCHING_GRID_DEFAULTS.gridMax
  ) {
    errors.tmgGridRowsErr =
      `Grid rows must be between ${TIMED_MATCHING_GRID_DEFAULTS.gridMin} ` +
      `and ${TIMED_MATCHING_GRID_DEFAULTS.gridMax}`
  }
  if (
    !Number.isInteger(Number(columns)) ||
    Number(columns) < TIMED_MATCHING_GRID_DEFAULTS.gridMin ||
    Number(columns) > TIMED_MATCHING_GRID_DEFAULTS.gridMax
  ) {
    errors.tmgGridColumnsErr =
      `Grid columns must be between ${TIMED_MATCHING_GRID_DEFAULTS.gridMin} ` +
      `and ${TIMED_MATCHING_GRID_DEFAULTS.gridMax}`
  }

  const timeNum = Number(timeLimit)
  if (!Number.isInteger(timeNum) || timeNum < TIMED_MATCHING_GRID_DEFAULTS.timeLimitSecondsMin) {
    errors.tmgTimeLimitErr = STRINGS.tmgTimeLimitMinErr
  } else if (timeNum > TIMED_MATCHING_GRID_DEFAULTS.timeLimitSecondsMax) {
    errors.tmgTimeLimitErr = STRINGS.tmgTimeLimitMaxErr
  }

  if (!isValidTimerDisplay(timerDisplay)) {
    errors.tmgTimerDisplayErr = 'Timer display must be countdown or countup'
  }

  if (!isValidInputType(inputType)) {
    errors.tmgInputTypeErr = 'Input type must be digit or checkmark'
  }

  const symbolKeyError = getTimedMatchingGridSymbolKeyError(details)
  if (symbolKeyError) {
    errors.tmgSymbolKeyErr = symbolKeyError
  }

  return errors
}

export const resizeItemSequence = (sequence, rows, columns) => {
  const total = getGridCellCount(rows, columns)
  const next = getEmptyItemSequence(rows, columns)
  if (!Array.isArray(sequence)) return next
  for (let i = 0; i < Math.min(sequence.length, total); i += 1) {
    next[i] = sequence[i]
  }
  return next
}
